import {
  AddCommentIconData,
  AddReactionIconData,
  ArrowOutwardIconData,
  BrushIconData,
  CategoryIconData,
  CircleIconData,
  Crop169IconData,
  GridOffIconData,
  GridOnIconData,
  MouseIconData,
  SelectAllIconData,
  StickyNote2IconData,
  SwapHorizIconData,
  TableChartIconData,
  TitleIconData,
} from "@meiden-connect/material-symbols";
import { BinarySwitch, Button, Icon } from "@meiden-connect/ui";
import { useEffect, useRef, type ReactNode } from "react";
import { EMOJI_STAMP_KINDS, type TextAlign } from "@refinement-canvas/domain";
import { useCanvasStore, type CanvasTool, type ShapeKind } from "../store/canvas-store";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { BOTTOM_TOOLBAR_WIDTH_VARIABLE, usePanelSizeReserve } from "../use-panel-size-reserve";
import { STAMP_KIND_GLYPH, STAMP_KIND_LABEL } from "../objects/stamp-object";
import { useCurrentActor } from "../../../auth/use-current-actor";
import { isSnapshotAvatarImageUrl } from "../../../auth/avatar-image-url";
import { CommentAvatar } from "../comments/comment-avatar";
import { SelectionStylePanel } from "./selection-style-panel";
import { TextRangeStylePanel } from "./text-range-style-panel";
import { TableCellStylePanel } from "./table-cell-style-panel";
import {
  ColorSwatchList,
  ColorValuePreview,
  PenThicknessChooser,
  ShapeStrokeWidthChooser,
  StrokePatternChooser,
  StrokePatternPreview,
  SubPanel,
} from "./toolbar-controls";
import {
  isTextStyleItemKey,
  renderTextStyleChild,
  textStyleAlignPreview,
  textStyleParentItems,
  TextStyleBoldButton,
  TEXT_STYLE_ITEM_LABELS,
  type TextStyleChildInput,
  type TextStyleItemKey,
} from "./text-style-panel-model";
import { ToolbarParentChildPanel, type ToolbarParentItem } from "./toolbar-parent-child-panel";
import { useShowToast } from "../notice/use-show-toast";
import {
  isValidHexColor,
  normalizeHexColor,
  PEN_PRESET_COLORS,
  PEN_THICKNESS_PIXELS,
  savePenColor,
  type PenColor,
} from "../../../lib/pen-preference";
import {
  saveStickyColor,
  STICKY_PRESET_COLORS,
  type StickyColor,
} from "../../../lib/sticky-preference";
import {
  resolveArrowStrokeColor,
  saveShapeFill,
  saveShapeStroke,
  SHAPE_FILL_NONE,
  SHAPE_FILL_PRESET_COLORS,
  SHAPE_STROKE_NONE,
  SHAPE_STROKE_PRESET_COLORS,
  SHAPE_STROKE_WIDTH_PIXELS,
  type ShapeFill,
} from "../../../lib/shape-preference";
import { SECTION_FILL_PRESET_COLORS } from "../../../lib/section-preference";
import { useCustomColorPalettes } from "./use-custom-color-palettes";
import { useShortcutHintNotices } from "./use-shortcut-hint-notices";

// 下部中央のフローティングツールバー。
// 図形・ペン・付箋・テキストの配置前設定は「メインツールバー → 親パネル → 子パネル」の
// 三階層で操作し、子パネルは常に最大 1 枚だけ開く（選択中オブジェクトのスタイル変更 UI と同じ方式）。
// 設定カテゴリが 1 つだけのセクション・スタンプは、従来どおり単一のサブパネルを直接表示する。
// ペン・付箋の色はボタンではなく丸い色サンプルの直接選択 UI とし、右端の「＋」で
// 任意色はエディタで明示確定して 1 件追加し、追加色は右クリックから変更・削除できる。
export function ToolBar(): JSX.Element {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const setShapeKind = useCanvasStore((s) => s.setShapeKind);
  const penColor = useCanvasStore((s) => s.penColor);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const penStrokePattern = useCanvasStore((s) => s.penStrokePattern);
  const setPenStrokePattern = useCanvasStore((s) => s.setPenStrokePattern);
  const penThickness = useCanvasStore((s) => s.penThickness);
  const setPenThickness = useCanvasStore((s) => s.setPenThickness);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);
  const shapeFill = useCanvasStore((s) => s.shapeFill);
  const setShapeFill = useCanvasStore((s) => s.setShapeFill);
  const shapeStroke = useCanvasStore((s) => s.shapeStroke);
  const setShapeStroke = useCanvasStore((s) => s.setShapeStroke);
  const shapeStrokePattern = useCanvasStore((s) => s.shapeStrokePattern);
  const setShapeStrokePattern = useCanvasStore((s) => s.setShapeStrokePattern);
  const shapeStrokeWidth = useCanvasStore((s) => s.shapeStrokeWidth);
  const setShapeStrokeWidth = useCanvasStore((s) => s.setShapeStrokeWidth);
  const sectionFill = useCanvasStore((s) => s.sectionFill);
  const setSectionFill = useCanvasStore((s) => s.setSectionFill);
  const stampKind = useCanvasStore((s) => s.stampKind);
  const setStampKind = useCanvasStore((s) => s.setStampKind);
  const textFontSize = useCanvasStore((s) => s.textFontSize);
  const setTextFontSize = useCanvasStore((s) => s.setTextFontSize);
  const textAlign = useCanvasStore((s) => s.textAlign);
  const setTextAlign = useCanvasStore((s) => s.setTextAlign);
  const textColor = useCanvasStore((s) => s.textColor);
  const setTextColor = useCanvasStore((s) => s.setTextColor);
  const textFill = useCanvasStore((s) => s.textFill);
  const setTextFill = useCanvasStore((s) => s.setTextFill);
  const textBold = useCanvasStore((s) => s.textBold);
  const setTextBold = useCanvasStore((s) => s.setTextBold);
  const gridSnapEnabled = useCanvasStore((s) => s.gridSnapEnabled);
  const setGridSnapEnabled = useCanvasStore((s) => s.setGridSnapEnabled);
  const invertSelectPan = useCanvasStore((s) => s.invertSelectPan);
  const setInvertSelectPan = useCanvasStore((s) => s.setInvertSelectPan);
  const showToast = useShowToast();
  const { notifyToolButtonClick } = useShortcutHintNotices();
  // ツールバー / サブパネル上での Ctrl+ホイールもキャンバスズームに振り替える（ブラウザ拡大を防ぐ）。
  const shellRef = useRef<HTMLDivElement | null>(null);
  // シェルは配置を安定させるため全幅にする。操作ヒントへ渡す予約幅は、サブパネルや
  // ビューポート幅ではなく、常に表示されるツールバー本体だけを実測する。
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  // 親の表示項目が消えてフォーカスの行き先が無くなったときの退避先（選択中のメインツールボタン）。
  const selectedToolButtonRef = useRef<HTMLButtonElement | null>(null);
  useCtrlWheelZoom(shellRef);
  // 左下の操作ヒントがこのツールバーへ食い込まないよう、実測幅を CSS 変数へ渡す。
  usePanelSizeReserve(toolbarRef, BOTTOM_TOOLBAR_WIDTH_VARIABLE, "width");

  // アバター画像を設定済みの利用者だけ、自分のアイコンをスタンプとして選べるようにする。
  // 「選べる条件」を「保存してよい条件」（data:image/ スナップショット）へ揃える。avatar スタンプは
  // 配置時点の自己完結スナップショットのみ保存するため、可変・失効し得るリモート https アバターの
  // 利用者には選択肢を出さない（出してもサーバで弾かれ、見た目も後から変わってしまうため）。
  // useCurrentActor().avatarImageUrl は usePortableAvatarImageUrl が idp-file: 等を縮小 data URL 化した
  // ポータブル値なので、典型的な IdP アバター（idp-file:）は解決後ここで data: と判定され候補に出る。
  const actor = useCurrentActor();
  const avatarStampAvailable = isSnapshotAvatarImageUrl(actor.avatarImageUrl);

  // アバターが利用不可へ遷移（再解決失敗・認証復旧中の一時欠落など）したのに stampKind が "avatar" の
  // ままだと、ボタンが非表示で選択状態が見えず、配置時に無言で既定の絵文字へフォールバックして
  // 「アバターを選んだのに別スタンプが置かれる」食い違いになる。利用不可になったら選択を既定の絵文字へ
  // 戻し、ツールバー表示と実際の生成物を一致させる。
  useEffect(() => {
    if (stampKind === "avatar" && !avatarStampAvailable) {
      setStampKind("thumbs-up");
    }
  }, [stampKind, avatarStampAvailable, setStampKind]);

  const palettes = useCustomColorPalettes();

  // ショートカットキーは use-global-shortcuts の単一キー割当と一致させる。
  // title 属性ではショートカットキーを併記して、利用者がキー操作に気付けるようにする。
  const buttons: { tool: CanvasTool; label: string; icon: string; shortcut: string }[] = [
    { tool: "place-sticky", label: "付箋を配置", icon: StickyNote2IconData, shortcut: "F" },
    { tool: "place-section", label: "セクションを配置", icon: SelectAllIconData, shortcut: "N" },
    { tool: "place-shape", label: "図形を配置", icon: CategoryIconData, shortcut: "R" },
    { tool: "place-text", label: "テキストを配置", icon: TitleIconData, shortcut: "T" },
    { tool: "draw-pen", label: "ペンで描画", icon: BrushIconData, shortcut: "M" },
    { tool: "place-table", label: "表を配置", icon: TableChartIconData, shortcut: "L" },
    { tool: "place-stamp", label: "スタンプを配置", icon: AddReactionIconData, shortcut: "E" },
    { tool: "place-comment", label: "コメントを追加", icon: AddCommentIconData, shortcut: "C" },
  ];

  const choosePenColor = (color: PenColor): void => {
    setPenColor(color);
    savePenColor(color);
  };
  const chooseStickyColor = (color: StickyColor): void => {
    setStickyColor(color);
    saveStickyColor(color);
  };
  const chooseShapeFill = (fill: ShapeFill): void => {
    setShapeFill(fill);
    saveShapeFill(fill);
  };
  const chooseShapeStroke = (stroke: string): void => {
    setShapeStroke(stroke);
    saveShapeStroke(stroke);
  };
  // 配置前のテキスト書式は store の setter が楽観更新と保存まで担う。setter が false を返すのは
  // 「永続化できなかった（＝ロールバック済み）」ときだけなので、その場合に共通のお知らせを出し、
  // 保存できていない値を「保存済み」に見せない。
  const notifyStyleSaveFailure = (): void => {
    showToast("warning", "設定を保存できませんでした。");
  };
  const chooseTextColor = (color: string): void => {
    if (!setTextColor(color)) notifyStyleSaveFailure();
  };
  const chooseTextFill = (fill: string): void => {
    if (!setTextFill(fill)) notifyStyleSaveFailure();
  };
  const chooseTextAlign = (align: TextAlign): void => {
    if (!setTextAlign(align)) notifyStyleSaveFailure();
  };
  const chooseTextFontSize = (px: number): void => {
    if (!setTextFontSize(px)) notifyStyleSaveFailure();
  };
  const toggleTextBold = (): void => {
    if (!setTextBold(!textBold)) notifyStyleSaveFailure();
  };
  // セクションの背景色は store の setSectionFill が localStorage への保存まで行う
  // （sticky / shape と異なり choose ヘルパーでの save 呼び出しは不要）。

  // カスタム色をパレットへ追加するだけの処理。選択編集パネルへも渡し、パレットの正本を
  // ToolBar 側の state 1 つに保つ（同一ドキュメント内では storage イベントが発火しないため、
  // 状態を二重に持つと「＋」で追加した色が片方へ反映されない）。
  const addPenColorToPalette = (color: string): void => {
    palettes.pen.add(color);
  };
  const addStickyColorToPalette = (color: string): void => {
    palettes.sticky.add(color);
  };
  const addShapeColorToPalette = (color: string): void => {
    palettes.shape.add(color);
  };
  const addShapeStrokeColorToPalette = (color: string): void => {
    palettes.shapeStroke.add(color);
  };
  const addSectionColorToPalette = (color: string): void => {
    palettes.section.add(color);
  };

  // 「＋」のエディタで確定した色だけを追加し、既定値へ昇格させる。
  const handleAddPenColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addPenColorToPalette(normalized);
    // 追加直後に選択状態へ昇格させ、利用者の意図どおりすぐ描画に使えるようにする。
    choosePenColor(normalized);
  };
  const handleAddStickyColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addStickyColorToPalette(normalized);
    chooseStickyColor(normalized);
  };
  const handleAddShapeColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addShapeColorToPalette(normalized);
    chooseShapeFill(normalized);
  };
  const handleAddShapeStrokeColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addShapeStrokeColorToPalette(normalized);
    chooseShapeStroke(normalized);
  };
  const handleAddSectionColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    addSectionColorToPalette(normalized);
    setSectionFill(normalized);
  };

  // 親トリガーのプレビューは store の現在値から毎 render 導出し、別 state を持たない。
  // 表示は選択中オブジェクト用の親パネルと同じ表現・同じクラスへそろえる。
  const colorPreview = (value: string): ReactNode => <ColorValuePreview value={value} />;
  const linePreview = (width: number): ReactNode => (
    <span className="canvas-selection-style-line" style={{ height: `${Math.min(width, 16)}px` }} />
  );
  const parentItem = (
    key: ToolSettingChildKey,
    label: string,
    preview: ReactNode
  ): ToolbarParentItem<ToolSettingChildKey> => ({ key, label, preview, mixed: false });

  // 矢印は塗りつぶせる領域を持たない（線＋矢じり）ため、背景色の項目と子パネルを出さない。
  const arrowShape = shapeKind === "arrow";
  const strokeLabel = arrowShape ? "線の色" : "枠線の色";
  const strokeWidthLabel = arrowShape ? "線の太さ" : "枠線の太さ";
  const shapeStrokePreviewColor = arrowShape ? resolveArrowStrokeColor(shapeStroke) : shapeStroke;
  const shapeKindIcon =
    SHAPE_OPTIONS.find((option) => option.kind === shapeKind)?.icon ?? CategoryIconData;

  // テキストの子パネルは選択後パネルと同じ共有モデルへ委譲する（項目・順序・選択肢の正本は 1 か所）。
  // 反映先だけが違い、ここでは「今後作るテキストの既定値」を更新する。
  const textChildInput: TextStyleChildInput = {
    testIdPrefix: "placement-style",
    ariaLabelPrefix: "テキストの",
    palettes,
    color: textColor,
    fill: textFill,
    textAlign,
    fontSize: textFontSize,
    onChooseColor: chooseTextColor,
    onChooseFill: chooseTextFill,
    onChooseAlign: chooseTextAlign,
    onChooseFontSize: chooseTextFontSize,
  };
  const renderToolSettingChild = (key: ToolSettingChildKey): ReactNode => {
    // 付箋の文字寄せもテキストと同じ子パネルを共有し、選択肢の差を作らない。
    if (isTextStyleItemKey(key)) return renderTextStyleChild(key, textChildInput);
    switch (key) {
      case "shape-kind":
        return (
          <SubPanel label="図形の種類" testId="placement-style-shape-kind">
            {SHAPE_OPTIONS.map((option) => (
              <Button
                key={option.kind}
                type="button"
                variant={shapeKind === option.kind ? "action emphasis" : "action"}
                className="canvas-toolbar-square-button"
                aria-label={option.label}
                aria-pressed={shapeKind === option.kind}
                title={option.label}
                onClick={() => setShapeKind(option.kind)}
              >
                <Icon d={option.icon} />
              </Button>
            ))}
          </SubPanel>
        );
      case "shape-fill":
        return (
          <SubPanel label="背景色" testId="placement-style-shape-fill">
            <ColorSwatchList
              ariaLabel="図形の背景色"
              presetColors={SHAPE_FILL_PRESET_COLORS}
              customColors={palettes.shape.colors}
              value={shapeFill}
              onChoose={chooseShapeFill}
              onAddColor={handleAddShapeColor}
              onUpdateCustomColor={palettes.shape.update}
              onDeleteCustomColor={palettes.shape.remove}
              noneOption={{ value: SHAPE_FILL_NONE, label: "背景なし" }}
            />
          </SubPanel>
        );
      case "shape-stroke":
        return (
          <SubPanel label={strokeLabel} testId="placement-style-shape-stroke">
            <ColorSwatchList
              ariaLabel={arrowShape ? "矢印の線の色" : "図形の枠線の色"}
              presetColors={SHAPE_STROKE_PRESET_COLORS}
              customColors={palettes.shapeStroke.colors}
              value={shapeStrokePreviewColor}
              onChoose={chooseShapeStroke}
              onAddColor={handleAddShapeStrokeColor}
              onUpdateCustomColor={palettes.shapeStroke.update}
              onDeleteCustomColor={palettes.shapeStroke.remove}
              noneOption={arrowShape ? undefined : { value: SHAPE_STROKE_NONE, label: "線なし" }}
            />
          </SubPanel>
        );
      case "shape-stroke-width":
        return (
          <SubPanel label={strokeWidthLabel} testId="placement-style-shape-stroke-width">
            <ShapeStrokeWidthChooser
              value={shapeStrokeWidth}
              color={resolveArrowStrokeColor(shapeStroke)}
              ariaLabelPrefix={strokeWidthLabel}
              onChange={setShapeStrokeWidth}
            />
          </SubPanel>
        );
      case "shape-stroke-pattern":
        return (
          <SubPanel
            label={arrowShape ? "線の種類" : "枠線の種類"}
            testId="placement-style-shape-stroke-pattern"
          >
            <StrokePatternChooser value={shapeStrokePattern} onChange={setShapeStrokePattern} />
          </SubPanel>
        );
      case "pen-color":
        return (
          <SubPanel label="ペンの色" testId="placement-style-pen-color">
            <ColorSwatchList
              ariaLabel="ペンの色"
              presetColors={PEN_PRESET_COLORS}
              customColors={palettes.pen.colors}
              value={penColor}
              onChoose={choosePenColor}
              onAddColor={handleAddPenColor}
              onUpdateCustomColor={palettes.pen.update}
              onDeleteCustomColor={palettes.pen.remove}
            />
          </SubPanel>
        );
      case "pen-thickness":
        return (
          <SubPanel label="太さ" testId="placement-style-pen-thickness">
            <PenThicknessChooser value={penThickness} color={penColor} onChange={setPenThickness} />
          </SubPanel>
        );
      case "pen-stroke-pattern":
        return (
          <SubPanel label="線の種類" testId="placement-style-pen-stroke-pattern">
            <StrokePatternChooser value={penStrokePattern} onChange={setPenStrokePattern} />
          </SubPanel>
        );
      case "sticky-color":
        return (
          <SubPanel label="付箋の色" testId="placement-style-sticky-color">
            <ColorSwatchList
              ariaLabel="付箋の色"
              presetColors={STICKY_PRESET_COLORS}
              customColors={palettes.sticky.colors}
              value={stickyColor}
              onChoose={chooseStickyColor}
              onAddColor={handleAddStickyColor}
              onUpdateCustomColor={palettes.sticky.update}
              onDeleteCustomColor={palettes.sticky.remove}
            />
          </SubPanel>
        );
    }
  };

  // 2 項目以上の設定を持つツールだけ親パネルを挟む。図形は種類によって項目数が変わる。
  const toolSettings: {
    readonly label: string;
    readonly items: readonly ToolbarParentItem<ToolSettingChildKey>[];
    /** 子パネルを開かない直接操作（テキストの太字）。項目の末尾へ並べる。 */
    readonly directActions?: ReactNode | undefined;
  } | null =
    tool === "place-shape"
      ? {
          label: "図形配置のスタイル",
          items: [
            parentItem("shape-kind", "図形の種類", <Icon d={shapeKindIcon} />),
            ...(arrowShape ? [] : [parentItem("shape-fill", "背景色", colorPreview(shapeFill))]),
            parentItem("shape-stroke", strokeLabel, colorPreview(shapeStrokePreviewColor)),
            parentItem(
              "shape-stroke-pattern",
              arrowShape ? "線の種類" : "枠線の種類",
              <StrokePatternPreview pattern={shapeStrokePattern} />
            ),
            parentItem(
              "shape-stroke-width",
              strokeWidthLabel,
              linePreview(SHAPE_STROKE_WIDTH_PIXELS[shapeStrokeWidth])
            ),
          ],
        }
      : tool === "draw-pen"
        ? {
            label: "ペン描画のスタイル",
            items: [
              parentItem("pen-color", "ペンの色", colorPreview(penColor)),
              parentItem(
                "pen-stroke-pattern",
                "線の種類",
                <StrokePatternPreview pattern={penStrokePattern} />
              ),
              parentItem("pen-thickness", "太さ", linePreview(PEN_THICKNESS_PIXELS[penThickness])),
            ],
          }
        : tool === "place-sticky"
          ? {
              label: "付箋配置のスタイル",
              items: [
                parentItem("sticky-color", "付箋の色", colorPreview(stickyColor)),
                parentItem(
                  "text-align",
                  TEXT_STYLE_ITEM_LABELS["text-align"],
                  textStyleAlignPreview(textAlign)
                ),
              ],
            }
          : tool === "place-text"
            ? {
                label: "テキスト配置のスタイル",
                // 項目・順序・プレビューは選択後パネルと同じ共有モデルから受け取る。
                items: textStyleParentItems({
                  color: textColor,
                  fill: textFill,
                  textAlign,
                  fontSize: textFontSize,
                }),
                // 太字は子を開かない直接操作なので、選択後パネルと同じく末尾へ置く。
                directActions: (
                  <TextStyleBoldButton
                    pressed={textBold}
                    testId="placement-style-bold"
                    onToggle={toggleTextBold}
                  />
                ),
              }
            : null;

  return (
    <div ref={shellRef} className="canvas-toolbar-shell" role="group" aria-label="キャンバスツール">
      {/* 選択中オブジェクトのスタイル変更パネル。表示可否は SelectionStylePanel 側が判定し、
          選択範囲の近傍へツールバーシェル外のポータルで描画する。 */}
      <SelectionStylePanel palettes={palettes} />
      {/* テキスト編集中の文字範囲専用パネル。表示可否は TextRangeStylePanel 側が判定し、
          ブラウザの選択矩形の近傍へポータルで描画する（全文用パネルとは相互排他）。 */}
      <TextRangeStylePanel palettes={palettes} />
      {/* 表のセル編集中のセル・範囲専用パネル。表示可否は TableCellStylePanel 側が判定し、
          編集中セルの矩形の近傍へポータルで描画する（表全体のパネルとは相互排他）。 */}
      <TableCellStylePanel palettes={palettes} />
      {/* DOM はメインツールバー → 親 → 子の論理順（＝Tab 順）で並べ、
          上から子 → 親 → メインという視覚順は CSS 側の並び替えで作る。 */}
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label="キャンバスツールバー"
        className="canvas-toolbar"
      >
        {/* グリッド吸着の ON/OFF。付箋ボタンの左に配置し、設定はローカルストレージに保存する。 */}
        <BinarySwitch
          aria-label="グリッド吸着の有効/無効"
          className="canvas-grid-snap-switch"
          data-testid="grid-snap-switch"
          onChange={setGridSnapEnabled}
          options={[
            { d: GridOffIconData, value: false },
            { d: GridOnIconData, value: true },
          ]}
          title={gridSnapEnabled ? "グリッド吸着: ON (G)" : "グリッド吸着: OFF (G)"}
          value={gridSnapEnabled}
        />
        {/* 選択/パン操作の左右反転。ON で左ドラッグ=範囲選択・右ドラッグ=キャンバス移動になる。 */}
        <BinarySwitch
          aria-label="選択/パン操作の左右反転"
          className="canvas-select-pan-invert-switch"
          data-testid="select-pan-invert-switch"
          onChange={setInvertSelectPan}
          options={[
            { d: MouseIconData, value: false },
            { d: SwapHorizIconData, value: true },
          ]}
          title={invertSelectPan ? "左右ドラッグ反転: ON (H)" : "左右ドラッグ反転: OFF (H)"}
          value={invertSelectPan}
        />
        {buttons.map((b) => (
          <Button
            key={b.tool}
            // 親の表示項目が 0 件になったときのフォーカス退避先として、選択中のボタンだけを保持する。
            ref={tool === b.tool ? selectedToolButtonRef : undefined}
            type="button"
            aria-label={b.label}
            aria-pressed={tool === b.tool}
            title={`${b.label} (${b.shortcut})`}
            onClick={() => {
              notifyToolButtonClick(tool, b);
              setTool(tool === b.tool ? "idle" : b.tool);
            }}
            data-testid={`tool-button-${b.tool}`}
            // クリック直後に配置が完了せず、配置モードへ入る準備操作なので intent を使う。
            // 選択中のツールは emphasis で強調し、現在のツールが分かるようにする。
            // 枠なし＋ホバー背景はデザインシステムのアイコンボタンに合わせて canvas-icon-button で付与する。
            // 外枠の正方形は canvas-toolbar-square-button が担い、状態表現とは責務を分ける。
            className="canvas-icon-button canvas-toolbar-square-button"
            variant={tool === b.tool ? "intent emphasis" : "intent"}
          >
            <Icon d={b.icon} />
          </Button>
        ))}
      </div>
      {toolSettings ? (
        <ToolbarParentChildPanel<ToolSettingChildKey>
          // メインツールを切り替えたら、旧ツールで開いていた子を持ち越さない。
          key={tool}
          label={toolSettings.label}
          items={toolSettings.items}
          renderChild={renderToolSettingChild}
          // おおもとのメインツールバーから見て親は上側に出るため、選択時の side="top" と同じ扱いにする。
          parentSide="top"
          testIdPrefix="placement-style"
          directActions={toolSettings.directActions}
          fallbackFocusRef={selectedToolButtonRef}
        />
      ) : null}
      {/* 設定カテゴリが 1 つだけのセクション・スタンプには、親パネルを挟まず単一パネルを直接出す。 */}
      {tool === "place-section" ? (
        <SubPanel label="背景色">
          <ColorSwatchList
            ariaLabel="セクションの背景色"
            presetColors={SECTION_FILL_PRESET_COLORS}
            customColors={palettes.section.colors}
            value={sectionFill}
            onChoose={setSectionFill}
            onAddColor={handleAddSectionColor}
            onUpdateCustomColor={palettes.section.update}
            onDeleteCustomColor={palettes.section.remove}
          />
        </SubPanel>
      ) : null}
      {tool === "place-stamp" ? (
        <SubPanel label="スタンプ">
          {/* 文言や DOM 階層に依存せず全スタンプボタンを列挙できるよう、一覧側へ安定した目印を置く。 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, max-content)",
              gap: "6px",
              alignItems: "center",
            }}
            data-testid="placement-stamp-list"
          >
            {EMOJI_STAMP_KINDS.map((kind) => (
              <Button
                key={kind}
                type="button"
                variant={stampKind === kind ? "action emphasis" : "action"}
                className="canvas-toolbar-square-button"
                aria-label={STAMP_KIND_LABEL[kind]}
                aria-pressed={stampKind === kind}
                title={STAMP_KIND_LABEL[kind]}
                data-testid={`placement-stamp-${kind}`}
                onClick={() => setStampKind(kind)}
              >
                <span style={{ fontSize: "18px", lineHeight: 1 }}>{STAMP_KIND_GLYPH[kind]}</span>
              </Button>
            ))}
            {/* アバター設定済みの利用者のみ、自分のアイコン画像をスタンプとして選択できる。 */}
            {avatarStampAvailable ? (
              <Button
                type="button"
                variant={stampKind === "avatar" ? "action emphasis" : "action"}
                className="canvas-toolbar-square-button"
                aria-label={STAMP_KIND_LABEL.avatar}
                aria-pressed={stampKind === "avatar"}
                title="自分のアバターをスタンプにする"
                data-testid="placement-stamp-avatar"
                onClick={() => setStampKind("avatar")}
              >
                <CommentAvatar
                  name={actor.displayName}
                  src={actor.avatarImageUrl}
                  size={20}
                  isSelf
                />
              </Button>
            ) : null}
          </div>
        </SubPanel>
      ) : null}
    </div>
  );
}

/** 配置前設定の子パネルを一意に指すキー。ツールごとに使う組み合わせが決まっている。 */
type ToolSettingChildKey =
  | "shape-kind"
  | "shape-fill"
  | "shape-stroke"
  | "shape-stroke-pattern"
  | "shape-stroke-width"
  | "pen-color"
  | "pen-stroke-pattern"
  | "pen-thickness"
  | "sticky-color"
  // テキストの項目キーは選択後パネルと共有し、片方だけへ項目が増える状態を作らない。
  | TextStyleItemKey;

const SHAPE_OPTIONS: readonly {
  readonly kind: ShapeKind;
  readonly label: string;
  readonly icon: string;
}[] = [
  { kind: "rectangle", label: "四角", icon: Crop169IconData },
  { kind: "circle", label: "丸", icon: CircleIconData },
  { kind: "arrow", label: "矢印", icon: ArrowOutwardIconData },
];
